import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, AlertTriangle, RefreshCw, Loader2, Activity,
  Calendar, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Calendar, label: 'Capacity Forecast', active: true },
  { icon: BarChart3, label: 'VBC Measures', href: '/admin/vbc/measures' },
];

export default function AdminCapacityForecast() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [byLine, setByLine] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/capacity-forecast`, getAuthHeader()),
        axios.get(`${API}/admin/capacity-forecast/by-line`, getAuthHeader()),
      ]);
      setData(a.data); setByLine(b.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load capacity forecast');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Capacity Forecast"
      subtitle="14-day demand forecast vs available clinician capacity. Spot shortfalls before referring hospitals do."
      testId="admin-capacity-forecast-page"
    >
      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}
      {!loading && data && (
        <>
          <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3">
            <Button onClick={load} className="cn-btn cn-btn-ghost !py-2" data-testid="capacity-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
            <span className="text-xs text-cn-text-muted ml-auto">Generated {new Date(data.generated_at).toLocaleString()}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="capacity-kpis">
            <Tile label="Active clinicians" value={k.active_clinicians} testid="capacity-kpi-active" />
            <Tile label="Avg appts / clin / day" value={k.avg_appts_per_clinician_per_day} testid="capacity-kpi-per-clin" />
            <Tile label="Daily capacity" value={k.daily_capacity} testid="capacity-kpi-capacity" />
            <Tile label="Days w/ shortfall" value={k.days_with_shortfall} tone={(k.days_with_shortfall || 0) > 0 ? 'red' : 'emerald'} testid="capacity-kpi-shortfall" />
            <Tile label="Peak demand" value={k.peak_demand_appts ? `${k.peak_demand_appts} appts` : '—'} sub={k.peak_demand_day} testid="capacity-kpi-peak" />
          </div>
          <div className="cn-card overflow-hidden mb-6" data-testid="capacity-series-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base">14-day forecast</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="capacity-series-table">
                <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                  <tr className="text-left">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-3 py-3 text-right">Forecast referrals</th>
                    <th className="px-3 py-3 text-right">Forecast appts</th>
                    <th className="px-3 py-3 text-right">Capacity</th>
                    <th className="px-3 py-3 text-right">Shortfall</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cn-border">
                  {(data.series || []).map((s) => (
                    <tr key={s.date} className={s.shortfall > 0 ? 'bg-red-50/30' : ''} data-testid={`capacity-row-${s.date}`}>
                      <td className="px-5 py-2.5 text-xs">{s.date}</td>
                      <td className="px-3 py-2.5 text-right text-xs">{s.forecast_referrals_in}</td>
                      <td className="px-3 py-2.5 text-right text-xs font-semibold">{s.forecast_appointments}</td>
                      <td className="px-3 py-2.5 text-right text-xs text-cn-text-muted">{s.available_capacity}</td>
                      <td className={`px-3 py-2.5 text-right text-xs font-semibold ${s.shortfall > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {s.shortfall > 0 ? `−${s.shortfall}` : '0'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {byLine && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="cn-card p-5" data-testid="capacity-byline-card">
                <h4 className="font-display font-semibold text-sm mb-3">Demand mix by line (last 14d)</h4>
                <div className="space-y-1.5">
                  {(byLine.by_line || []).slice(0, 8).map((l) => (
                    <div key={l.line} className="flex items-center justify-between text-xs">
                      <span className="text-cn-text capitalize">{l.line}</span>
                      <span className="font-semibold text-cn-text">{l.count} · {l.share_pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="cn-card p-5" data-testid="capacity-bystate-card">
                <h4 className="font-display font-semibold text-sm mb-3">Demand by state</h4>
                <div className="space-y-1.5">
                  {(byLine.by_state || []).slice(0, 8).map((s) => (
                    <div key={s.state} className="flex items-center justify-between text-xs">
                      <span className="text-cn-text">{s.state}</span>
                      <span className="font-semibold text-cn-text">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}

function Tile({ label, value, tone = 'neutral', testid, sub }) {
  const cls = { neutral: 'text-cn-text', emerald: 'text-emerald-700', red: 'text-red-700' }[tone];
  return (
    <div className="cn-card p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
      <p className={`font-display font-bold text-2xl ${cls}`}>{value ?? 0}</p>
      {sub && <p className="text-[10px] text-cn-text-muted mt-0.5">{sub}</p>}
    </div>
  );
}
